import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from "@tanstack/react-query";
import { type Api } from "api-client";
import { createContext, useContext } from "react";
import { type PropsWithChildren, useMemo, useState } from "react";

import { useExercisesRepository } from "src/contexts/ExercisesRepositoryContext";

export interface ExerciseSearchFilters {
  query: string;
  muscles: string[];
}

export interface ExerciseSearchContextValue {
  query: UseInfiniteQueryResult<InfiniteData<Api.ListExercises.Output.Self>>;
  filters: ExerciseSearchFilters;
  setFilters: (filters: Partial<ExerciseSearchFilters>) => void;
}

export const exerciseSearchContext = createContext<ExerciseSearchContextValue>(
  null!,
);

export function useExerciseSearchContext() {
  return useContext(exerciseSearchContext);
}

export interface ExerciseSearchContextProviderProps extends PropsWithChildren {
  enabled: boolean;
}
export function ExerciseSearchContextProvider(
  props: ExerciseSearchContextProviderProps,
) {
  const exercisesRepository = useExercisesRepository();
  const [filters, setFilters] = useState<ExerciseSearchFilters>({
    query: "",
    muscles: [],
  });

  const exerciseSearchQuery = useInfiniteQuery({
    queryKey: [...exercisesRepository.rootKey, "search", filters] as const,
    queryFn: ({ pageParam }) => {
      return exercisesRepository.list({
        q: filters.query,
        pageIndex: pageParam,
        pageSize: 30,
        muscles: filters.muscles.length > 0 ? filters.muscles : undefined,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pageInfo.hasNextPage) {
        return undefined;
      }

      return lastPage.pageInfo.pageIndex + 1;
    },
    placeholderData: (previousData) => previousData,
    enabled: props.enabled,
  });

  const value = useMemo<ExerciseSearchContextValue>(() => {
    return {
      query: exerciseSearchQuery,
      filters: filters,
      setFilters: (next) => {
        setFilters((filters) => {
          return {
            ...filters,
            ...next,
          };
        });
      },
    };
  }, [exerciseSearchQuery, filters, setFilters]);

  return (
    <exerciseSearchContext.Provider value={value}>
      {props.children}
    </exerciseSearchContext.Provider>
  );
}
